import { Button } from '@/ui/Button';
import { cn } from '@/lib/cn';

/**
 * EstadoVacio — el vacío tiene que decir POR QUÉ está vacío.
 *
 * Es el mismo criterio que ya usa `Utils.emptyState()` en 60 sitios del portal
 * vanilla, portado tal cual porque está bien pensado. El ejemplo que lo explica
 * mejor está en Solicitudes: no es lo mismo «no has enviado nada nunca» que
 * «no tienes nada en curso, pero sí 14 cerradas». El segundo mensaje evita que
 * alguien piense que se le perdieron los datos.
 *
 * Y separa los dos vacíos que en el sistema anterior se veían igual:
 *
 *   `vacio`   — no hay nada creado todavía. Ofrece crearlo.
 *   `filtro`  — sí hay datos, pero tu filtro no encontró ninguno. Ofrece
 *               limpiar el filtro, que es lo que la persona necesita, no crear
 *               un registro nuevo.
 *
 * Confundirlos manda a alguien a crear algo que ya tenía delante.
 */
export function EstadoVacio({
  icono, titulo, descripcion, accion, compacto, className,
}: {
  /** Clase de Bootstrap Icons, p. ej. `bi-inbox`. */
  icono: string;
  titulo: string;
  /** Por qué está vacío y qué pasa ahora. Opcional solo cuando el título ya lo
   *  dice entero. */
  descripcion?: string;
  accion?: { texto: string; onClick: () => void };
  /** Dentro de una tarjeta o un panel, donde no hay sitio para el bloque grande. */
  compacto?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center text-center',
        compacto ? 'gap-2 px-4 py-6' : 'gap-3 px-6 py-12',
        className,
      )}
    >
      {/* El icono es decorativo: lo que informa es el texto, y repetirlo en el
          lector de pantalla solo alarga la lectura. */}
      <i
        className={cn('bi', icono, compacto ? 'text-6' : 'text-9', 'text-muted opacity-60')}
        aria-hidden="true"
      />
      <p className={cn('font-semibold text-ink', compacto ? 'text-3' : 'text-5')}>{titulo}</p>
      {descripcion && (
        <p className="max-w-prose text-3 leading-relaxed text-muted">{descripcion}</p>
      )}
      {accion && (
        <Button variant="primary" size={compacto ? 'sm' : 'md'} className="mt-1" onClick={accion.onClick}>
          {accion.texto}
        </Button>
      )}
    </div>
  );
}

/** El vacío de un filtro. Dice cuántos hay en total —el dato que falta para
 *  entender que no se perdió nada— y ofrece quitar el filtro. */
export function EstadoFiltroVacio({
  total, que = 'resultados', onLimpiar,
}: {
  total: number;
  que?: string;
  onLimpiar: () => void;
}) {
  return (
    <EstadoVacio
      icono="bi-funnel"
      titulo={`Ningún ${que.replace(/s$/, '')} coincide con el filtro`}
      descripcion={
        total > 0
          ? `Hay ${total} ${que} en total. Prueba a quitar algún filtro.`
          : 'Prueba a quitar algún filtro.'
      }
      accion={{ texto: 'Quitar filtros', onClick: onLimpiar }}
    />
  );
}
